import {PayloadAction} from '@reduxjs/toolkit';
import * as sentry from '@sentry/react';
import {call, put, select, takeLatest} from 'redux-saga/effects';

import i18n from 'config/i18n/i18n';
import {
  getBasketCalculation,
  getBasketPromotionsLite,
  updateBasketItemPreview,
  changeBasketQuantity,
  getMetaDataV5,
  updateMetaDataV5,
  removePromotionVoucher
} from 'services/basket/basket-service';
import {fetchBasketVouchers} from 'services/campaign/campaign-service';
import SendBrowserEvent from 'services/send-browser-event';
import {setAlertAction} from 'store/actions/alert-action';
import {setCurrentStoreAction} from 'store/actions/app-action';
import {
  getBasketCalculationAction,
  getBasketPromotionsLiteAction,
  removeUnavailableItemsAction,
  setBasketDetailsAction,
  setBasketPromotionsLiteAction,
  setIsBasketApiFailedAction,
  updatePreviewItemQuantityAction,
  deleteSelectedItemsAction,
  setBasketPromotionsLiteLoadingAction,
  batchUpdateItemsQuantityAction,
  bulkUpdateBasketItemsPreviewAction,
  getRM3PromotionsAction,
  setRM3PromotionsAction,
  getMetaDataAction,
  updateMetaDataAction,
  fetchBasketVouchersAction,
  setBasketVouchersAction,
  removePromotionVoucherAction,
  setRecommendationsComplexPromotionAction,
  selectItemIdsAction,
  unselectItemIdsAction,
  setSelectedItemsAction,
  checkedUnselectItemIdsAction
} from 'store/actions/basket-action';
import {AlertType, AppMode} from 'store/types/app';
import {AppState} from 'store/types/app';
import {AuthState} from 'store/types/auth';
import {
  BasketCalculation,
  BasketItemInput,
  BasketRoot,
  GetMetaDataPayload,
  ItemToUpdateQuantity,
  MetaDataResponse,
  OmniChannelVoucher,
  PromoAdvice,
  PromotionPayload,
  ReduxBasketState,
  RemovePromotionVoucherPayload,
  TCalculateBasketPayload,
  UpdateMetaDataPayload
} from 'types/basket';
import {BasketVoucherDetails, FetchBasketVouchersQuery} from 'types/campaign';
import {
  findBasketItemByVariantId,
  getIdsFromBasketItems,
  getItemDetailsForPromotions,
  getPreviewItemsWithBasketItems
} from 'utils';
import {extractStoreInfoData} from 'utils/helper';
import {selectedItemsLocalStorage} from 'utils/LocalStorage';

import {getRmPromotions} from '../../services/loyalty/loyalty-service';

function* getBasketCalculationsSaga(action?: {payload?: BasketCalculation}): Generator {
  try {
    const payload = action?.payload;
    const lang = i18n.language;
    const selectedItems = selectedItemsLocalStorage.get();
    yield put(setIsBasketApiFailedAction(false));
    const params: any = yield select((state) => {
      const {currentStore, defaultMembershipCard, appMode} = state?.app || {};
      const idToken = state.auth?.idToken;
      const customerId = state.auth?.customerId;

      return {
        makroMemberCard: defaultMembershipCard,
        zipCode: currentStore?.zipcode,
        subDistrict: currentStore?.subdistrict,
        lang,
        idToken,
        isExpress: appMode === AppMode.express,
        basketItems: payload?.isSelectAllItems ? undefined : selectedItems,
        customerId
      };
    });
    const basketResult = yield call(getBasketCalculation, params as TCalculateBasketPayload);

    const basket = basketResult as BasketRoot;
    const allItemIds = basket.basket?.basketItems?.map((item) => item.productDetails.id);

    yield put(
      setBasketDetailsAction({
        ...basket.basket,
        basketItemsFromFe: payload?.isSelectAllItems ? allItemIds : basket.basket?.basketItemsFromFe
      })
    );
    yield put(getBasketPromotionsLiteAction());
    if (basket?.basket?.basketItems && basket?.basket?.basketItems.length) {
      const allItemsInBasket = getIdsFromBasketItems(basket.basket.basketItems);
      const filteredSelectedItems = payload?.isSelectAllItems
        ? allItemsInBasket
        : allItemsInBasket.filter((productId: string) => selectedItems.includes(productId));
      selectedItemsLocalStorage.set(filteredSelectedItems);
      yield put(setSelectedItemsAction(filteredSelectedItems));
    }
    if (
      payload?.isBasketPreviewItemsUpdated &&
      basket?.basket?.unavailableItems &&
      basket?.basket?.basketItems &&
      !basket?.basket?.error?.hasError
    ) {
      const previewItems = getPreviewItemsWithBasketItems([
        ...(basket?.basket?.basketItems ?? []),
        ...(basket?.basket?.unavailableItems ?? [])
      ]);
      SendBrowserEvent.basketContentUpdated(previewItems);
    }
    // checking if the current calculation of omni voucher channel have quantity more than 1
    const appliedVoucher = basket.basket?.appliedVoucherPromotion?.[0];
    if (appliedVoucher) {
      const appliedItemSku = appliedVoucher.skus[0]; // 250718: the omnivoucher can only be applied to single SKU only
      const basketItemWhereVoucherIsApplied = basket.basket?.basketItems?.find(
        (item) => item.productDetails.sku === appliedItemSku
      );
      if (basketItemWhereVoucherIsApplied && basketItemWhereVoucherIsApplied.quantity > 1) {
        yield put(
          setAlertAction({
            type: AlertType.voucherQuantityLimitExceeded,
            details: {
              productName: basketItemWhereVoucherIsApplied.productDetails.title
            }
          })
        );
        return;
      }
    }
    const invalidVoucher = basket.basket?.invalidVoucherPromotion?.[0];
    // currently we can't check if the item that the voucher applied to is removed from PDP
    // thus if we see that the voucher is invalid and item is not exist, we assume it got removed outside
    // of basket MFE
    // in this case we agreed on just remove voucher right away without showing any message
    // HY 2025/9/11
    const basketItemForVoucher = basket.basket?.basketItems.find(
      (record) => record.productDetails.sku === invalidVoucher?.skus[0]
    );
    const isInvalidVoucherBecauseOfRemovedFromPdp = invalidVoucher && !basketItemForVoucher;
    if (invalidVoucher) {
      if (isInvalidVoucherBecauseOfRemovedFromPdp) {
        // no need recalculate, it's already excluded anyway
        yield call(() => removePromotionVoucherSaga({payload: {skipRecalculation: true}}));
      } else {
        yield put(
          setAlertAction({
            type: AlertType.voucherExpired
          })
        );
        yield put(setIsBasketApiFailedAction(true));
        return;
      }
    }
    if (basket?.basket?.error) {
      yield put(setIsBasketApiFailedAction(true));
    }
  } catch (e: any) {
    sentry.captureException(e);
    yield put(setIsBasketApiFailedAction(true));
  }
}

function* updateItemQuantityWithPreviewSaga(action: {payload: any}): Generator {
  try {
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;

    const selectedItems = selectedItemsLocalStorage.get();

    const {idToken, productId, quantity, variantId} = action.payload;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;

    const basketItem = findBasketItemByVariantId(basket?.basketItems || [], variantId);
    const unavailableBasketItem = findBasketItemByVariantId(basket?.unavailableItems || [], variantId);

    const allBasketItems = [...(basket?.basketItems || []), ...(basket?.unavailableItems || [])];

    const isLastItem = allBasketItems.length === 1 && quantity === 0;
    if (quantity === 0) {
      if (basketItem) {
        //deleting from available items
        const newBasketItems = basket?.basketItems.filter((item) => item.productId !== basketItem?.productId);
        yield put(setBasketDetailsAction({...basket, basketItems: newBasketItems}));
      } else if (unavailableBasketItem) {
        //deleting from un-available items
        const newUnavailableItems = basket?.unavailableItems.filter(
          (item) => item.productId !== unavailableBasketItem?.productId
        );
        yield put(setBasketDetailsAction({...basket, unavailableItems: newUnavailableItems}));
      }
      if (selectedItems?.includes(productId)) {
        const filteredSelectedItems = selectedItems?.filter((itm: string) => itm !== productId);
        selectedItemsLocalStorage.set(filteredSelectedItems);
        yield put(setSelectedItemsAction(filteredSelectedItems));
      }
    }

    const updateBasketItem = yield call(
      () =>
        updateBasketItemPreview(
          [
            {
              productId,
              quantity
            }
          ],
          idToken || '',
          isExpress
        ) as any
    );
    const result = updateBasketItem as any;
    if (result?.data?.basketItems) {
      SendBrowserEvent.basketContentUpdated(result.data.basketItems);
      yield !isLastItem && put(getBasketCalculationAction());
    } else {
      if (result?.error?.code) {
        SendBrowserEvent.showErrorToast(result.error);
      }
      if (quantity === 0) {
        yield put(setBasketDetailsAction(basket));
      }
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* removeUnavailableItemsSaga(action: {payload: (isOOSProductsDeleted?: boolean) => Promise<void>}): Generator {
  try {
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    const selectedItems = selectedItemsLocalStorage.get();
    const onCheckoutClick = action.payload;
    const idsToBeRemoved: string[] = [];
    const bulkDeleteItemsArray: BasketItemInput[] = basket?.unavailableItems.map((item) => {
      idsToBeRemoved.push(item?.productId);
      return {
        productId: item.productId,
        quantity: 0
      };
    }) as any;
    const filteredSelectedItems = selectedItems?.filter((itm: string) => !idsToBeRemoved.includes(itm));
    yield call(() => getBasketCalculationsSaga());
    const updateBasketItem = yield call(
      () => updateBasketItemPreview(bulkDeleteItemsArray, idToken || '', isExpress) as any
    );
    const result = updateBasketItem as any;
    if (result?.data) {
      yield call(() => SendBrowserEvent.basketContentUpdated(result.data.basketItems));
      yield call(() => onCheckoutClick(true));
    }
    selectedItemsLocalStorage.set(filteredSelectedItems);
    yield put(setSelectedItemsAction(filteredSelectedItems));
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* deleteSelectedItemsSaga(): Generator {
  try {
    const selectedItems = selectedItemsLocalStorage.get();
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;

    const bulkDeleteItemsArray: BasketItemInput[] = selectedItems?.map((id: string) => {
      return {
        productId: id,
        quantity: 0
      };
    }) as any;
    if (bulkDeleteItemsArray.length) {
      const updateBasketItem = yield call(
        () => updateBasketItemPreview(bulkDeleteItemsArray, idToken || '', isExpress) as any
      );
      const result = updateBasketItem as any;
      if (result?.data) {
        yield call(() => getBasketCalculationsSaga());
        yield call(() => SendBrowserEvent.basketContentUpdated(result.data.basketItems));
        selectedItemsLocalStorage.set([]);
      }
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* getBasketPromotionsLiteSaga(): Generator {
  try {
    yield put(setBasketPromotionsLiteLoadingAction(true));
    const {defaultMembershipCard} = (yield select((state) => state?.app) as unknown) as AppState;
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const basketItemsCount = basket?.basketItems?.length;
    // Allow promotions API to be called even when there are unavailable items
    // Only require that there are some available items in the basket
    if (defaultMembershipCard && basketItemsCount && basketItemsCount > 0) {
      const data = yield call(() => {
        const itemDetails = getItemDetailsForPromotions(basket?.basketItems, basket.basketItemsFromFe ?? [], true);
        if (itemDetails?.length) {
          return getBasketPromotionsLite(
            {
              storeId: basket?.storeCode ?? '',
              customerId: defaultMembershipCard?.cardNumber ?? '',
              itemDetails
            },
            idToken
          );
        }
      });

      yield put(setBasketPromotionsLiteAction(data));
    }
  } catch (e: any) {
    yield put(
      setBasketPromotionsLiteAction({
        skuBasedPromotions: [],
        promotionSuggestions: [],
        estimatedPointsReceive: 0,
        estimatedPointsDetails: []
      })
    );
    sentry.captureException(e);
  } finally {
    yield put(setBasketPromotionsLiteLoadingAction(false));
  }
}

function* getRM3PromotionsSaga(action: PayloadAction<PromotionPayload>): Generator {
  try {
    const {rm3Promotions: currentPromotion} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;

    const {currentStore} = (yield select((state) => state.app)) as AppState;
    const storeCode = currentStore?.storeCodes ?? [];

    const response = (yield call(() => getRmPromotions(action.payload.ids, storeCode))) as any;

    const mergedPromotions: Record<string, {promoAdvice: PromoAdvice[]; loading: boolean}> = {
      ...currentPromotion,
      ...response
    };
    yield put(setRecommendationsComplexPromotionAction(mergedPromotions));
    yield put(setRM3PromotionsAction(mergedPromotions));
  } catch (e) {
    sentry.captureException(e);
  }
}

function* batchUpdateItemsQuantitySaga(action: {payload: ItemToUpdateQuantity[]}): Generator {
  try {
    const itemsToUpdate = action.payload;
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const {basket} = (yield select((state) => state.basket) as unknown) as ReduxBasketState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!basket) return;
    const basketToUpdate = {...basket}; // ensure no mutiation of original basket object

    for (const itemToUpdate of itemsToUpdate) {
      const items = changeBasketQuantity(basketToUpdate, {
        quantity: itemToUpdate.quantity,
        variantId: itemToUpdate.variantId
      });
      basketToUpdate.basketItems = items;
    }

    const updated = yield call(
      () => updateBasketItemPreview(basketToUpdate.basketItems || [], idToken || '', isExpress) as any
    );
    if (updated) yield call(() => getBasketCalculationsSaga());
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* bulkUpdateBasketItemsPreviewSaga(action: {payload: BasketItemInput[]}): Generator {
  try {
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    const itemsToUpdate = action.payload;
    const updateBasketItem = yield call(
      () => updateBasketItemPreview(itemsToUpdate || [], idToken || '', isExpress) as any
    );
    const result = updateBasketItem as any;
    if (result?.data) {
      yield call(() => getBasketCalculationsSaga());
      yield call(() => SendBrowserEvent.basketContentUpdated(result.data.basketItems));
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* updateMetaDataSaga(action: {payload: UpdateMetaDataPayload}): Generator {
  try {
    const {address, storeInfo} = action.payload;
    const idToken = (yield select((state) => state.auth?.idToken) as unknown) as string;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!idToken) return;

    const response = (yield call(
      updateMetaDataV5,
      storeInfo?.zipcode || '',
      storeInfo?.subdistrict || '',
      idToken || '',
      storeInfo?.stores || [],
      address?.id || '',
      address
        ? {
            address,
            subdistrictEn: address.subDistrict?.nameEn,
            subdistrictTh: address.subDistrict?.nameTh,
            subdistrictId: address.subDistrict?.id
          }
        : {},
      isExpress
    )) as MetaDataResponse;

    if (response?.status === 'SUCCESS') {
      const {isSameDayDelivery, showStorePickup, storeCodes} = extractStoreInfoData(response);
      yield put(
        setCurrentStoreAction({
          zipcode: response?.zipCode,
          subdistrict: response?.subDistrict,
          storeCodes: storeCodes,
          showStorePickup: showStorePickup,
          isSameDayDelivery: isSameDayDelivery,
          addressId: response?.addressId
        })
      );
      yield put(getBasketCalculationAction());
    }
  } catch (e: any) {
    console.error('Error at updateMetaDataSaga v5:', e);
    sentry.captureException(e);
  }
}

function* getMetaDataSaga(action: {payload: GetMetaDataPayload | undefined}): Generator {
  try {
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!idToken) return;

    const response = (yield call(getMetaDataV5, idToken, isExpress) as unknown) as MetaDataResponse;

    if (response?.status === 'SUCCESS') {
      const {isSameDayDelivery, showStorePickup, storeCodes} = extractStoreInfoData(response);
      yield put(
        setCurrentStoreAction({
          zipcode: response?.zipCode,
          subdistrict: response?.subDistrict,
          subdistrictEn: response?.metadata?.subdistrictEn,
          subdistrictTh: response?.metadata?.subdistrictTh,
          storeCodes: storeCodes,
          showStorePickup: showStorePickup,
          isSameDayDelivery: isSameDayDelivery,
          addressId: response?.addressId
        })
      );

      if (action?.payload?.callCalculate) {
        yield put(getBasketCalculationAction({isBasketPreviewItemsUpdated: true, isSelectAllItems: true}));
      }
    }
  } catch (e: any) {
    console.error('Error at getMetaDataSaga v5:', e);
    sentry.captureException(e);
  }
}

function* fetchBasketVouchersSaga(action: {payload: FetchBasketVouchersQuery}): Generator {
  try {
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    if (!idToken) return;

    const response = (yield call(fetchBasketVouchers, idToken, action.payload) as unknown) as BasketVoucherDetails;

    yield put(setBasketVouchersAction(response));
  } catch (e: any) {
    console.error('Error at getMetaDataSaga v5:', e);
    sentry.captureException(e);
  }
}
function* removePromotionVoucherSaga(action: {payload: RemovePromotionVoucherPayload}): Generator {
  const {callback, skipRecalculation} = action.payload;
  try {
    const {idToken} = (yield select((state) => state.auth) as unknown) as AuthState;
    const isExpress = ((yield select((state) => state.app.appMode)) as AppMode) === AppMode.express;
    if (!idToken) return;
    yield call(removePromotionVoucher, idToken, isExpress);
    if (!skipRecalculation) {
      yield call(() => getBasketCalculationsSaga());
    }
    if (callback) {
      yield call(callback);
    }
  } catch (e: any) {
    console.error('Error at removePromotionVoucherSaga:', e);
    sentry.captureException(e);
  }
}

function* selectItemsSaga(action: {payload: string[]}) {
  const currentSelectedItemIds = (yield select((state) => state.basket.selectedItems) as unknown) as string[];
  const newItemIds = action.payload;
  const filteredNewItemIds = newItemIds.filter((item) => !currentSelectedItemIds.includes(item));
  if (filteredNewItemIds.length === 0) return;
  const newSelectedItemIds = [...currentSelectedItemIds, ...filteredNewItemIds];
  yield put(setSelectedItemsAction(newSelectedItemIds));
  selectedItemsLocalStorage.set(newSelectedItemIds);
  yield call(() => getBasketCalculationsSaga());
}

function* checkedUnselectItemsSaga(action: {payload: string[]}) {
  const currentSelectedItemIds = (yield select((state) => state.basket.selectedItems) as unknown) as string[];
  const removedItemIds = action.payload;
  const filteredRemovedItemIds = removedItemIds.filter((item) => currentSelectedItemIds.includes(item));
  const currentOmniVouchers =
    ((yield select((state) => state.basket.basket.appliedVoucherPromotion) as unknown) as OmniChannelVoucher[]) || [];
  const currentOmniVoucher = currentOmniVouchers[0];
  if (!currentOmniVoucher) {
    yield call(() => unselectItemsSaga({payload: filteredRemovedItemIds}));
    return;
  }
  const isRemovedItemsContainOmniVoucherItem = currentOmniVoucher.productIds.some((productId: string) =>
    filteredRemovedItemIds.includes(productId)
  );
  if (!isRemovedItemsContainOmniVoucherItem) {
    yield call(() => unselectItemsSaga({payload: filteredRemovedItemIds}));
    return;
  }
  yield put(
    setAlertAction({
      type: AlertType.voucherItemDeselected,
      details: {
        removedItemIds: filteredRemovedItemIds
      }
    })
  );
}

function* unselectItemsSaga(action: {payload: string[]}) {
  const currentSelectedItemIds = (yield select((state) => state.basket.selectedItems) as unknown) as string[];
  const removedItemIds = action.payload;
  const filteredItemIds = currentSelectedItemIds.filter((item) => !removedItemIds.includes(item));
  if (filteredItemIds.length === currentSelectedItemIds.length) return;
  yield put(setSelectedItemsAction(filteredItemIds));
  selectedItemsLocalStorage.set(filteredItemIds);
  yield call(() => getBasketCalculationsSaga());
}

export default function* () {
  yield takeLatest(getBasketCalculationAction, getBasketCalculationsSaga);
  yield takeLatest(updatePreviewItemQuantityAction, updateItemQuantityWithPreviewSaga);
  yield takeLatest(removeUnavailableItemsAction, removeUnavailableItemsSaga);
  yield takeLatest(removePromotionVoucherAction, removePromotionVoucherSaga);
  yield takeLatest(deleteSelectedItemsAction, deleteSelectedItemsSaga);
  yield takeLatest(getBasketPromotionsLiteAction, getBasketPromotionsLiteSaga);
  yield takeLatest(getRM3PromotionsAction, getRM3PromotionsSaga);
  yield takeLatest(batchUpdateItemsQuantityAction, batchUpdateItemsQuantitySaga);
  yield takeLatest(bulkUpdateBasketItemsPreviewAction, bulkUpdateBasketItemsPreviewSaga);
  yield takeLatest(updateMetaDataAction, updateMetaDataSaga);
  yield takeLatest(getMetaDataAction, getMetaDataSaga);
  yield takeLatest(fetchBasketVouchersAction, fetchBasketVouchersSaga);
  yield takeLatest(selectItemIdsAction, selectItemsSaga);
  yield takeLatest(unselectItemIdsAction, unselectItemsSaga);
  yield takeLatest(checkedUnselectItemIdsAction, checkedUnselectItemsSaga);
}
